import React, {useCallback, useState} from "react";
import {InputType} from "../../constants/inputTypes";
import Tippy from "@tippyjs/react";
import Icon from "../ui/Icon";

interface TextInputProps {
    id?: string;
    name: string;
    value: string;
    type?: InputType;
    required?: boolean;
    label?: string;
    placeHolder?: string;
    disabled?: boolean;
    infoMessage?: string;
    errorMessage?: string;
    onChange: (name: string, value: string, isValid: boolean) => void;
    onValidate?: (value: string) => boolean;
}

export const TextInput = (
    {
        id,
        name,
        value,
        type = InputType.Text,
        required = false,
        label,
        placeHolder = "",
        disabled = false,
        infoMessage,
        errorMessage,
        onChange,
        onValidate,
    }: TextInputProps) => {

    const [isError, setIsError] = useState(false);
    const [isTouch, setIsTouch] = useState(false);
    const [isFocus, setIsFocus] = useState(false);

    const isRequired = isTouch && required && !value.trim();

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setIsTouch(true);
            const currentValue = e.target.value;
            const hasValue = currentValue.trim().length > 0;
            const nativeValid = e.target.validity.valid;
            const customValid = onValidate ? onValidate(currentValue) : true;
            const valid = hasValue
                ? nativeValid && customValid
                : !required;
            setIsError(hasValue && !valid);
            onChange(name, currentValue, valid);
        },
        [onChange, name, onValidate, required],
    );

    const fieldClass = [
        "field",
        required && "required",
        !isFocus && isRequired && "is-required",
        !isFocus && isError && "is-error"
    ].filter(Boolean).join(" ");

    return (
        <div className={fieldClass}>
            <label htmlFor={id}>{label}</label>
            <div className="input-icon-wrap">
                <input id={id}
                       className="input"
                       type={type}
                       placeholder={!disabled ? placeHolder : ""}
                       required={required}
                       disabled={disabled}
                       name={name}
                       value={value}
                       onChange={handleChange}
                       onFocus={() => setIsFocus(true)}
                       onBlur={() => setIsFocus(false)}/>
                {infoMessage && (
                    <Tippy content={infoMessage} placement="top" className="ph-tooltip">
                        <div className="input-icon-toggle">
                            <Icon name="info" size={16}/>
                        </div>
                    </Tippy>
                )}
            </div>
            {errorMessage && !isFocus && (isRequired || isError) && (
                <div className="field-msg err" role="alert">
                        <span className="ico">
                            <Icon name="warn" size={13}/>
                        </span>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};